import React from "react";
import { Marker as GoogleMarker, OverlayView, InfoWindow } from "@react-google-maps/api";
import { Marker as MapboxMarker } from "react-map-gl";
import { deriveHydrantDocKey } from "../../utils/globalPoiUtils";
import { hydrantIconSvg, hydrantIconDataUri, HYDRANT_ICON_W, HYDRANT_ICON_H } from "../../utils/hydrantIcon";
import callmarker from "./call.png";
import callmarkerfire from "./firepin.png";
import callmarkerems from "./emspin.png";

// The pieces of the legacy full-screen alarm map, shared by the legacy board
// (FullScreenMap) and the Station Board Studio alarm map widget so both draw
// the incident view exactly the same way. The markup and styles here are the
// legacy ones, moved as-is.

export const MAPBOX_INCIDENT_STYLE = "mapbox://styles/mapbox/satellite-streets-v12";
export const MAPBOX_INCIDENT_ZOOM = 18;
export const GOOGLE_INCIDENT_ZOOM = 20;
export const GOOGLE_INCIDENT_OPTIONS = { mapTypeId: "hybrid", trafficLayer: true };
// How long each active alarm stays on screen before the map moves to the next.
export const ALARM_CYCLE_MS = 12000;
// Each engine creates one DOM/overlay node per marker, so hydrants are capped,
// nearest-to-center first.
export const MAX_HYDRANT_MARKERS = 150;

export const getMarkerIcon = (callcard) => {
  switch (callcard) {
    case "FIRE":
      return callmarkerfire;
    case "EMS":
      return callmarkerems;
    default:
      return callmarker;
  }
};

export const hasValidCoords = (lat, lng) =>
  lat !== null && lng !== null && lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng);

// Caller location from CAD event comments: the first "<lng> <lat>" decimal pair.
export function extractCallerCoordinates(eventComments) {
  if (!eventComments) return null;
  const matches = String(eventComments).match(/-?\d+\.\d+\s+-?\d+\.\d+/);
  if (!matches) return null;
  const [longitudeStr, latitudeStr] = matches[0].split(/\s+/);
  const latitude = parseFloat(latitudeStr);
  const longitude = parseFloat(longitudeStr);
  return isNaN(latitude) || isNaN(longitude) ? null : { latitude, longitude };
}

export function nearestHydrants(hydrants, center, max = MAX_HYDRANT_MARKERS) {
  if (!center) return [];
  return hydrants
    .filter((h) => Number.isFinite(Number(h.latitude)) && Number.isFinite(Number(h.longitude)))
    .map((h) => ({ h, d: (h.latitude - center.lat) ** 2 + (h.longitude - center.lng) ** 2 }))
    .sort((a, b) => a.d - b.d)
    .slice(0, max)
    .map((x) => x.h);
}

// Pulses used by the unit and caller markers on both engines.
export const MapAnimations = () => (
  <style>
    {`
      @keyframes vehiclePulse {
        0% {
          transform: translate(-50%, -50%) scale(0.5);
          opacity: 0.8;
        }
        70% {
          transform: translate(-50%, -50%) scale(2);
          opacity: 0;
        }
        100% {
          transform: translate(-50%, -50%) scale(0.5);
          opacity: 0;
        }
      }
      @keyframes callerPulse {
        0% {
          transform: translate(-50%, -50%) scale(0.8);
          opacity: 0.9;
        }
        70% {
          transform: translate(-50%, -50%) scale(1.5);
          opacity: 0.3;
        }
        100% {
          transform: translate(-50%, -50%) scale(0.8);
          opacity: 0.9;
        }
      }
    `}
  </style>
);

// ---- shared marker bodies ----------------------------------------------------

const VehicleBody = ({ vehicle, onSelect, mapbox }) => (
  <div
    style={{ position: "relative", width: "50px", height: "50px", ...(mapbox ? { cursor: "pointer" } : {}) }}
    onClick={() => onSelect && onSelect(vehicle)}
  >
    <div style={{
      position: "absolute",
      width: "50px",
      height: "50px",
      borderRadius: "50%",
      backgroundColor: "rgba(255, 0, 0, 0.4)",
      transform: "translate(-50%, -50%)",
      animation: "vehiclePulse 2s infinite",
    }} />
    <div style={{
      position: "absolute",
      width: "12px",
      height: "12px",
      backgroundColor: "#ff0000",
      border: "2px solid white",
      borderRadius: "50%",
      transform: "translate(-50%, -50%)",
      ...(mapbox ? { left: "50%", top: "50%" } : {}),
    }} />
    <div style={{
      position: "absolute",
      top: mapbox ? "30px" : "20px",
      left: mapbox ? "50%" : "15%",
      transform: "translateX(-50%)",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      color: "white",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      whiteSpace: "nowrap",
      fontWeight: "bold",
    }}>
      {vehicle.responderName}
    </div>
  </div>
);

const CallerBody = ({ mapbox }) => {
  const centered = mapbox ? { left: "50%", top: "50%" } : {};
  return (
    <div style={{ position: "relative" }}>
      <div style={{
        position: "absolute",
        width: "60px",
        height: "60px",
        borderRadius: "50%",
        backgroundColor: "rgba(0, 255, 0, 0.3)",
        border: "3px solid #00ff00",
        transform: "translate(-50%, -50%)",
        animation: "callerPulse 2s infinite",
        ...centered,
      }} />
      <div style={{
        position: "absolute",
        width: "12px",
        height: "12px",
        backgroundColor: "#00ff00",
        border: "2px solid white",
        borderRadius: "50%",
        transform: "translate(-50%, -50%)",
        ...centered,
      }} />
      <div style={{
        position: "absolute",
        width: "2px",
        height: "30px",
        backgroundColor: "#00ff00",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }} />
      <div style={{
        position: "absolute",
        width: "30px",
        height: "2px",
        backgroundColor: "#00ff00",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }} />
      <div style={{
        position: "absolute",
        top: "35px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        color: "#00ff00",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        whiteSpace: "nowrap",
        fontWeight: "bold",
        border: "1px solid #00ff00",
      }}>
        Caller Location
      </div>
    </div>
  );
};

const validAlarms = (alarms) => alarms.filter((a) => hasValidCoords(a.lat, a.lng));
const validVehicles = (vehicles) => vehicles.filter((v) => hasValidCoords(v.latitude, v.longitude));
const validCaller = (loc) => !!loc && hasValidCoords(loc.latitude, loc.longitude);

// ---- Mapbox (default engine) ---------------------------------------------------

export function MapboxIncidentLayers({ alarms, hydrants, vehicles, callerLocation, selectedVehicle, onSelectVehicle }) {
  return (
    <>
      {validAlarms(alarms).map((alarm, index) => (
        <MapboxMarker key={alarm.id} longitude={alarm.lng} latitude={alarm.lat} anchor="bottom">
          <img
            src={getMarkerIcon(alarm.callcard)}
            alt={`Alarm ${index + 1}`}
            style={{ width: "50px", height: "50px", cursor: "pointer" }}
          />
        </MapboxMarker>
      ))}

      {hydrants.map((h, i) => (
        <MapboxMarker
          key={`hyd-${deriveHydrantDocKey(h) || i}`}
          longitude={Number(h.longitude)}
          latitude={Number(h.latitude)}
          anchor="bottom"
        >
          <div
            title={h.poiTitle || h.HYDRANT_ID || "Hydrant"}
            style={{
              width: `${HYDRANT_ICON_W}px`,
              height: `${HYDRANT_ICON_H}px`,
              filter: "drop-shadow(0 1px 1.5px rgba(0,0,0,0.5))",
              cursor: "default",
            }}
            dangerouslySetInnerHTML={{ __html: hydrantIconSvg() }}
          />
        </MapboxMarker>
      ))}

      {validVehicles(vehicles).map((vehicle) => (
        <MapboxMarker key={vehicle.responderName} longitude={vehicle.longitude} latitude={vehicle.latitude} anchor="center">
          <VehicleBody vehicle={vehicle} onSelect={onSelectVehicle} mapbox />
        </MapboxMarker>
      ))}

      {validCaller(callerLocation) && (
        <MapboxMarker longitude={callerLocation.longitude} latitude={callerLocation.latitude} anchor="center">
          <CallerBody mapbox />
        </MapboxMarker>
      )}

      {selectedVehicle && selectedVehicle.type !== "aircraft" && (
        <MapboxMarker longitude={selectedVehicle.longitude} latitude={selectedVehicle.latitude} anchor="bottom" offset={[0, -20]}>
          <div style={{
            backgroundColor: "#212c40",
            color: "white",
            padding: "10px",
            borderRadius: "5px",
            minWidth: "200px",
            position: "relative",
          }}>
            <button
              onClick={() => onSelectVehicle && onSelectVehicle(null)}
              style={{
                position: "absolute",
                top: "5px",
                right: "5px",
                background: "transparent",
                border: "none",
                color: "white",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              ×
            </button>
            <h4 style={{ margin: "0 0 5px 0", color: "white" }}>{selectedVehicle.responderName}</h4>
            <p style={{ margin: "3px 0" }}><strong>Type:</strong> {selectedVehicle.vehicletype}</p>
            <p style={{ margin: "3px 0" }}><strong>Last Updated:</strong> {new Date(selectedVehicle.timestamp).toLocaleString()}</p>
          </div>
        </MapboxMarker>
      )}
    </>
  );
}

// ---- Google Maps (per-board setting, and failover when Mapbox errors) ----------

export function GoogleIncidentLayers({ alarms, hydrants, vehicles, callerLocation, selectedVehicle, onSelectVehicle }) {
  return (
    <>
      {validAlarms(alarms).map((alarm, index) => (
        <GoogleMarker
          key={alarm.id}
          position={{ lat: alarm.lat, lng: alarm.lng }}
          title={`Alarm ${index + 1}`}
          icon={{ url: getMarkerIcon(alarm.callcard), scaledSize: new window.google.maps.Size(50, 50) }}
        />
      ))}

      {hydrants.map((h, i) => (
        <GoogleMarker
          key={`hyd-${deriveHydrantDocKey(h) || i}`}
          position={{ lat: Number(h.latitude), lng: Number(h.longitude) }}
          title={h.poiTitle || h.HYDRANT_ID || "Hydrant"}
          icon={{
            url: hydrantIconDataUri(),
            scaledSize: new window.google.maps.Size(HYDRANT_ICON_W, HYDRANT_ICON_H),
            anchor: new window.google.maps.Point(HYDRANT_ICON_W / 2, HYDRANT_ICON_H),
          }}
        />
      ))}

      {validVehicles(vehicles).map((vehicle) => (
        <OverlayView
          key={vehicle.responderName}
          position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <VehicleBody vehicle={vehicle} onSelect={onSelectVehicle} />
        </OverlayView>
      ))}

      {selectedVehicle && selectedVehicle.type !== "aircraft" && (
        <InfoWindow
          position={{ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude }}
          onCloseClick={() => onSelectVehicle && onSelectVehicle(null)}
        >
          <div style={{ backgroundColor: "#212c40", color: "white", padding: "10px", borderRadius: "5px" }}>
            <h4 style={{ margin: 0, color: "white" }}>{selectedVehicle.responderName}</h4>
            <p style={{ margin: 0 }}><strong>Type:</strong> {selectedVehicle.vehicletype}</p>
            <p style={{ margin: 0 }}><strong>Last Updated:</strong> {new Date(selectedVehicle.timestamp).toLocaleString()}</p>
          </div>
        </InfoWindow>
      )}

      {validCaller(callerLocation) && (
        <OverlayView
          position={{ lat: callerLocation.latitude, lng: callerLocation.longitude }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <CallerBody />
        </OverlayView>
      )}
    </>
  );
}

// ---- screens shown instead of the map -------------------------------------------

// The current alarm has no usable address: agency logo and a notice.
export const NoMappingScreen = ({ logoUrl, style }) => (
  <div
    style={{
      width: "100vw",
      height: "100vh",
      backgroundColor: "#42474a",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      padding: "20px",
      ...style,
    }}
  >
    {logoUrl ? (
      <>
        <img src={logoUrl} alt="Agency Logo" style={{ maxWidth: "80%", maxHeight: "80%", marginBottom: "20px" }} />
        <p style={{ color: "white", fontSize: "20px", textAlign: "center", maxWidth: "80%" }}>
          Mapping is not available for this alarm
        </p>
      </>
    ) : (
      <p style={{ color: "white" }}>Loading agency logo...</p>
    )}
  </div>
);

export const LoadingMapScreen = ({ style }) => (
  <div
    style={{
      width: "100vw",
      height: "100vh",
      backgroundColor: "#42474a",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      ...style,
    }}
  >
    <p style={{ color: "white" }}>Loading map...</p>
  </div>
);
